import React, { useCallback } from 'react'
import { graphql, useFragment } from 'react-relay'
import { useNavigate } from 'react-router-dom'

import { UserPrimaryPhoto } from '@newComponents/User/UserPrimaryPhoto'
import { FormattedMarkdown } from '@newComponents/FormattedMarkdown/FormattedMarkdown'
import { Icon } from '@newComponents/Icon'
import { Link } from '@newComponents/Link'
import { Text, H2 } from '@newComponents/Text'
import { Button, useCreateLinkPressHandler } from '@newComponents'

import { XStack, YStack } from 'tamagui'

import { PresentationInfo_presentation$key } from './__generated__/PresentationInfo_presentation.graphql'

export const PresentationInfo = (props: {
  presentation: PresentationInfo_presentation$key
}) => {
  const navigate = useNavigate()
  const presentation = useFragment(
    graphql`
      fragment PresentationInfo_presentation on Presentation {
        canEdit
        description
        presenterFirstName
        presenterLastName
        prettyUrl
        title

        presenter {
          slugId
          firstName
          lastName

          ...UserPrimaryPhotoNew_user
        }
      }
    `,
    props.presentation
  )
  const linkAction = useCallback(
    () => navigate(`/presentations/${presentation.prettyUrl}/settings`),
    [presentation.prettyUrl]
  )
  const { linkPressHandler, anchorRef } = useCreateLinkPressHandler(linkAction)

  let presenterElement

  if (presentation.presenter) {
    presenterElement = (
      <YStack>
        <Text size='$4' color='$colorMuted'>
          Presentation by
        </Text>
        <Link
          size='$4'
          href={`/${presentation.presenter.slugId}`}
          fontWeight='$5'
        >
          {presentation.presenter?.firstName} {presentation.presenter?.lastName}
        </Link>
      </YStack>
    )
  } else {
    presenterElement = (
      <YStack>
        <Text size='$4' color='$colorMuted'>
          Presentation by
        </Text>

        <Text size='$4' fontWeight='$5'>
          {presentation.presenterFirstName} {presentation.presenterLastName}
        </Text>
      </YStack>
    )
  }

  return (
    <YStack gap='$4'>
      <XStack>
        <XStack flex={1} gap='$3'>
          {presentation.presenter && (
            <Link href={`/${presentation.presenter.slugId}`}>
              <UserPrimaryPhoto user={presentation.presenter} size='$12' />
            </Link>
          )}

          <YStack flex={1} gap='$2'>
            <H2>{presentation.title}</H2>

            {presenterElement}
          </YStack>
          {!!presentation.canEdit && (
            <Button
              ref={anchorRef as any}
              tag='a'
              href={`/presentations/${presentation.prettyUrl}/settings`}
              secondary
              gap='$1'
              paddingVertical='$2'
              paddingHorizontal='$2'
              minHeight='unset'
              height={32}
              borderRadius={16}
              onPress={linkPressHandler}
              errorFlash={false}
            >
              <Icon name='settings-filled' color='$color' size='$4' />

              <Button.Text secondary size='$4'>
                Settings
              </Button.Text>
            </Button>
          )}
        </XStack>
      </XStack>

      {!!presentation.description && (
        <FormattedMarkdown textSize='$5'>
          {presentation.description}
        </FormattedMarkdown>
      )}
    </YStack>
  )
}

export default PresentationInfo
